import { useEffect, useState } from "react";
import { Check, X, Truck, RefreshCw } from "lucide-react";
import { StatusBadge } from "../DashboardShell";
import { toast } from "sonner";
import api, { eur } from "../../lib/api";
import MobileShopOrders from "./MobileShopOrders";

const FILTERS = [["in_attesa", "In attesa"], ["approvato", "Approvati"], ["consegnato", "Consegnati"], ["all", "Tutti"]];

export default function MobileOrders({ initialSource }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("in_attesa");
  const [openId, setOpenId] = useState(null);
  const [source, setSource] = useState(initialSource || new URLSearchParams(window.location.search).get("source") || "agenti");

  const load = () => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/orders/${id}/status`, { status }); toast.success("Ordine aggiornato"); load(); }
    catch { toast.error("Aggiornamento non riuscito"); }
  };

  const list = orders.filter((o) => filter === "all" || o.status === filter);
  const pending = orders.filter((o) => o.status === "in_attesa").length;

  return (
    <div data-testid="mobile-orders">
      <div className="inline-flex bg-stone-100 rounded-full p-1 mb-4 w-full" data-testid="mobile-orders-source">
        <button onClick={() => setSource("agenti")} data-testid="mobile-source-agenti" className={`flex-1 h-9 rounded-full text-sm font-semibold ${source === "agenti" ? "bg-white shadow text-[#0F4C81]" : "text-stone-500"}`}>Agenti</button>
        <button onClick={() => setSource("negozio")} data-testid="mobile-source-negozio" className={`flex-1 h-9 rounded-full text-sm font-semibold ${source === "negozio" ? "bg-white shadow text-[#0F4C81]" : "text-stone-500"}`}>Negozio online</button>
      </div>
      {source === "negozio" ? <MobileShopOrders initialFilter={initialSource === "negozio" ? "AWAITING_TRANSFER" : undefined} /> : (<>
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-2xl font-semibold text-[#0B2A45]">Ordini {pending > 0 && <span className="text-sm bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 align-middle" data-testid="mobile-orders-pending">{pending} da approvare</span>}</h1>
        <button onClick={load} className="p-2 text-stone-500" data-testid="mobile-orders-refresh"><RefreshCw className="w-5 h-5" /></button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-4 px-4" data-testid="mobile-orders-filters">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`mobile-orders-filter-${v}`}
            className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold ${filter === v ? "bg-[#0F4C81] text-white" : "bg-white border border-stone-200 text-stone-600"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-center text-stone-400 py-10">Nessun ordine</p>}
        {list.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`mobile-order-${o.id}`}>
            <button onClick={() => setOpenId(openId === o.id ? null : o.id)} className="w-full text-left" data-testid={`mobile-order-toggle-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0B2A45] truncate">{o.client_name}</div>
                  <div className="text-xs text-stone-500">{o.agent_name} · {new Date(o.created_at).toLocaleDateString("it-IT")}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold text-[#0F4C81]">{eur(o.total)}</div>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            </button>
            {openId === o.id && (
              <div className="mt-3 pt-3 border-t border-stone-100 text-sm space-y-1" data-testid={`mobile-order-items-${o.id}`}>
                {(o.items || []).map((it, i) => (
                  <div key={`${it.product_id || it.name}-${i}`} className="flex justify-between text-stone-600"><span>{it.quantity} × {it.name}</span><span className="font-mono">{eur(it.line_total)}</span></div>
                ))}
                <div className="flex justify-between text-xs text-stone-400 pt-1"><span>Provvigione {o.commission_rate}%</span><span className="font-mono text-[#15803D]">{eur(o.commission_amount)}</span></div>
                {o.notes && <p className="text-xs text-stone-500 italic pt-1">{o.notes}</p>}
              </div>
            )}
            <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
              {o.status === "in_attesa" && (
                <>
                  <button onClick={() => setStatus(o.id, "approvato")} className="flex-1 h-11 rounded-xl bg-[#0F4C81] text-white font-semibold flex items-center justify-center gap-1" data-testid={`mobile-approve-${o.id}`}><Check className="w-4 h-4" />Approva</button>
                  <button onClick={() => setStatus(o.id, "rifiutato")} className="h-11 px-4 rounded-xl bg-red-50 text-red-600 font-semibold flex items-center justify-center gap-1" data-testid={`mobile-reject-${o.id}`}><X className="w-4 h-4" />Rifiuta</button>
                </>
              )}
              {o.status === "approvato" && (
                <button onClick={() => setStatus(o.id, "consegnato")} className="flex-1 h-11 rounded-xl bg-[#15803D] text-white font-semibold flex items-center justify-center gap-1" data-testid={`mobile-deliver-${o.id}`}><Truck className="w-4 h-4" />Segna consegnato</button>
              )}
              {(o.status === "consegnato" || o.status === "rifiutato") && (
                <button onClick={() => setStatus(o.id, "in_attesa")} className="flex-1 h-11 rounded-xl bg-stone-50 text-stone-600 text-sm" data-testid={`mobile-reopen-${o.id}`}>Riporta in attesa</button>
              )}
            </div>
          </div>
        ))}
      </div>
      </>)}
    </div>
  );
}
